import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { extractPdfTextInBrowser, isPdfFile } from "@/lib/extractPdfText";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Upload, Link, X } from "lucide-react";

interface UploadMaterialDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  workspaceId?: string;
  onSuccess?: () => void;
}

const MAX_FILES_PER_UPLOAD = 20;
const ALLOWED_FILE_EXT = /\.(pdf|pptx|docx)$/i;

function sanitizeStorageSegment(name: string): string {
  return name.replace(/[^a-zA-Z0-9.-]/g, "_").slice(0, 180) || "file";
}

const UploadMaterialDialog = ({ open, onOpenChange, projectId, workspaceId, onSuccess }: UploadMaterialDialogProps) => {
  const [files, setFiles] = useState<File[]>([]);
  const [urls, setUrls] = useState("");
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("upload");
  const { toast } = useToast();

  const extractFileNameFromUrl = (urlString: string): string => {
    try {
      const urlObj = new URL(urlString);
      const pathname = urlObj.pathname;
      const segments = pathname.split('/').filter(Boolean);
      if (segments.length > 0) {
        return decodeURIComponent(segments[segments.length - 1]) || urlObj.hostname;
      }
      return urlObj.hostname;
    } catch {
      return urlString.slice(0, 50);
    }
  };

  // Maximum URL length to prevent abuse
  const MAX_URL_LENGTH = 2048;
  const MAX_URLS_PER_BATCH = 20;

  // Private/internal IP patterns to block (SSRF prevention)
  const BLOCKED_PATTERNS = [
    /^https?:\/\/localhost/i,
    /^https?:\/\/127\./i,
    /^https?:\/\/10\./i,
    /^https?:\/\/172\.(1[6-9]|2[0-9]|3[0-1])\./i,
    /^https?:\/\/192\.168\./i,
    /^https?:\/\/0\./i,
    /^https?:\/\/\[::1\]/i,
    /^https?:\/\/\[fd/i,
    /^https?:\/\/169\.254\./i, // Link-local
  ];

  const isBlockedUrl = (url: string): boolean => {
    return BLOCKED_PATTERNS.some(pattern => pattern.test(url));
  };

  const parseUrls = (input: string): { valid: string[]; errors: string[] } => {
    const lines = input.split('\n').map(line => line.trim()).filter(line => line.length > 0);
    const valid: string[] = [];
    const errors: string[] = [];

    for (const line of lines) {
      if (line.length > MAX_URL_LENGTH) {
        errors.push(`URL too long (max ${MAX_URL_LENGTH} chars): ${line.slice(0, 50)}...`);
        continue;
      }
      if (!line.startsWith('http://') && !line.startsWith('https://')) {
        errors.push(`Invalid protocol: ${line.slice(0, 50)}`);
        continue;
      }
      if (isBlockedUrl(line)) {
        errors.push(`Blocked URL (internal/private): ${line.slice(0, 50)}`);
        continue;
      }
      try {
        new URL(line); // Validate URL format
        valid.push(line);
      } catch {
        errors.push(`Invalid URL format: ${line.slice(0, 50)}`);
      }
    }

    return { valid: valid.slice(0, MAX_URLS_PER_BATCH), errors };
  };

  const handleFilesSelected = (list: FileList | null) => {
    if (!list?.length) return;
    const picked = Array.from(list);
    const accepted: File[] = [];
    const rejected: string[] = [];
    for (const f of picked) {
      if (ALLOWED_FILE_EXT.test(f.name)) {
        accepted.push(f);
      } else {
        rejected.push(f.name);
      }
    }
    if (rejected.length > 0) {
      toast({
        variant: "destructive",
        title: "Unsupported file type",
        description:
          `${rejected[0]} is not PDF, PPTX, or DOCX` +
          (rejected.length > 1 ? ` (+${rejected.length - 1} more)` : ""),
      });
    }
    if (accepted.length === 0) return;
    setFiles((prev) => {
      const combined = [...prev, ...accepted];
      if (combined.length > MAX_FILES_PER_UPLOAD) {
        toast({
          variant: "destructive",
          title: "Too many files",
          description: `You can add up to ${MAX_FILES_PER_UPLOAD} files per upload. Extra files were not added.`,
        });
        return combined.slice(0, MAX_FILES_PER_UPLOAD);
      }
      return combined;
    });
  };

  const removeFileAt = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleFileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (files.length === 0) return;

    setLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const storageFolder = workspaceId || projectId;
      let successCount = 0;
      let errorCount = 0;
      let totalPdfChars = 0;

      for (let i = 0; i < files.length; i++) {
        const file = files[i]!;
        try {
          const fileExt = file.name.split(".").pop() || "bin";
          const safeBase = sanitizeStorageSegment(file.name.replace(/\.[^.]+$/, ""));
          const storagePath = `${user.id}/${storageFolder}/${Date.now()}-${i}-${safeBase}.${fileExt}`;

          const { error: uploadError } = await supabase.storage
            .from("materials")
            .upload(storagePath, file);

          if (uploadError) throw uploadError;

          const { data: material, error: insertError } = await supabase
            .from("materials")
            .insert({
              project_id: projectId || null,
              workspace_id: workspaceId || null,
              file_name: file.name,
              file_type: file.type,
              storage_path: storagePath,
            })
            .select()
            .single();

          if (insertError) throw insertError;

          let clientExtractedText: string | null = null;
          if (isPdfFile(file)) {
            clientExtractedText = await extractPdfTextInBrowser(file);
            if (clientExtractedText) {
              totalPdfChars += clientExtractedText.length;
            }
          }

          supabase.functions
            .invoke("process-material", {
              body: {
                materialId: material.id,
                storagePath,
                ...(clientExtractedText && { clientExtractedText }),
              },
            })
            .catch((err) => {
              console.error("Background processing error:", err);
            });

          successCount++;
        } catch (err) {
          console.error("Upload error for file:", file.name, err);
          errorCount++;
        }
      }

      if (successCount > 0) {
        const pdfHint =
          totalPdfChars > 0
            ? ` Extracted ${totalPdfChars.toLocaleString()} characters from PDFs;`
            : "";
        toast({
          title: "Success",
          description:
            `Uploaded ${successCount} file${successCount > 1 ? "s" : ""}.${pdfHint} Processing in background…` +
            (errorCount > 0 ? ` (${errorCount} failed)` : ""),
        });

        resetAndClose();

        window.dispatchEvent(new CustomEvent("materials-updated"));

        if (onSuccess) {
          onSuccess();
        }
      } else {
        toast({
          variant: "destructive",
          title: "Upload failed",
          description: "Could not upload any files. Check your connection and try again.",
        });
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast({
        variant: "destructive",
        title: "Error uploading materials",
        description: error instanceof Error ? error.message : "Upload failed",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUrlSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const { valid: urlList, errors: urlErrors } = parseUrls(urls);
    
    if (urlErrors.length > 0) {
      toast({
        variant: "destructive",
        title: "Invalid URLs detected",
        description: urlErrors[0] + (urlErrors.length > 1 ? ` (+${urlErrors.length - 1} more)` : ''),
      });
    }
    
    if (urlList.length === 0) return;

    setLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      let successCount = 0;
      let errorCount = 0;

      for (const url of urlList) {
        try {
          const fileName = extractFileNameFromUrl(url);

          const { data: material, error: insertError } = await supabase
            .from("materials")
            .insert({
              project_id: projectId || null,
              workspace_id: workspaceId || null,
              file_name: fileName,
              file_type: "url",
              storage_path: url,
            })
            .select()
            .single();

          if (insertError) throw insertError;

          supabase.functions.invoke('process-material', {
            body: {
              materialId: material.id,
              storagePath: url,
              isUrl: true,
            }
          }).catch(err => {
            console.error('Background processing error:', err);
          });

          successCount++;
        } catch (err) {
          console.error('Error adding URL:', url, err);
          errorCount++;
        }
      }

      if (successCount > 0) {
        toast({
          title: "Success",
          description: `Added ${successCount} URL${successCount > 1 ? 's' : ''}${errorCount > 0 ? ` (${errorCount} failed)` : ''}. Processing in background...`,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to add any URLs",
        });
      }
      
      resetAndClose();
      
      // Dispatch custom event to refresh materials list
      window.dispatchEvent(new CustomEvent("materials-updated"));
      
      if (onSuccess) {
        onSuccess();
      }
      
    } catch (error) {
      console.error('URL add error:', error);
      toast({
        variant: "destructive",
        title: "Error adding URLs",
        description: error instanceof Error ? error.message : "Failed to add URLs",
      });
    } finally {
      setLoading(false);
    }
  };

  const resetAndClose = () => {
    setFiles([]);
    setUrls("");
    setActiveTab("upload");
    onOpenChange(false);
  };

  const { valid: validUrls } = parseUrls(urls);
  const urlCount = validUrls.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Educational Material</DialogTitle>
          <DialogDescription>
            Upload files (multiple at once) or add URLs to educational content
          </DialogDescription>
        </DialogHeader>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="upload" className="flex items-center gap-2">
              <Upload className="h-4 w-4" />
              Upload files
            </TabsTrigger>
            <TabsTrigger value="url" className="flex items-center gap-2">
              <Link className="h-4 w-4" />
              Add URLs
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="upload">
            <form onSubmit={handleFileSubmit}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="file">Files</Label>
                  <Input
                    id="file"
                    type="file"
                    accept=".pdf,.pptx,.docx"
                    multiple
                    onChange={(e) => {
                      handleFilesSelected(e.target.files);
                      e.target.value = "";
                    }}
                  />
                  <p className="text-sm text-muted-foreground">
                    PDF, PPTX, or DOCX — select multiple files (up to {MAX_FILES_PER_UPLOAD} per upload).
                  </p>
                  {files.length > 0 && (
                    <ul className="max-h-40 space-y-1.5 overflow-y-auto rounded-md border border-border bg-muted/30 p-2 text-sm">
                      {files.map((f, i) => (
                        <li
                          key={`${f.name}-${f.size}-${i}`}
                          className="flex items-center gap-2 rounded px-1 py-0.5"
                        >
                          <span className="min-w-0 flex-1 truncate" title={f.name}>
                            {f.name}
                          </span>
                          <button
                            type="button"
                            onClick={() => removeFileAt(i)}
                            className="shrink-0 rounded p-1 text-muted-foreground hover:bg-muted hover:text-destructive"
                            aria-label={`Remove ${f.name}`}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={resetAndClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={loading || files.length === 0}>
                  {loading
                    ? "Uploading..."
                    : files.length > 1
                      ? `Upload ${files.length} files`
                      : "Upload"}
                </Button>
              </DialogFooter>
            </form>
          </TabsContent>
          
          <TabsContent value="url">
            <form onSubmit={handleUrlSubmit}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="urls">URLs (one per line)</Label>
                  <Textarea
                    id="urls"
                    placeholder="https://example.com/document1.pdf&#10;https://example.com/document2.pdf&#10;https://example.com/article"
                    value={urls}
                    onChange={(e) => setUrls(e.target.value)}
                    rows={5}
                    className="font-mono text-sm"
                  />
                  <p className="text-sm text-muted-foreground">
                    {urlCount > 0 ? `${urlCount} valid URL${urlCount > 1 ? 's' : ''} detected` : 'Enter URLs to online educational resources'}
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={resetAndClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={loading || urlCount === 0}>
                  {loading ? "Adding..." : `Add ${urlCount > 0 ? urlCount : ''} URL${urlCount !== 1 ? 's' : ''}`}
                </Button>
              </DialogFooter>
            </form>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default UploadMaterialDialog;
